import React, { useEffect, useState } from 'react';
import { X, Loader2, NotebookPen } from 'lucide-react';
import type { DemandaEsteira } from '../../../types';
import { FILAS, formatDia } from './filas';

interface Props {
    demanda: DemandaEsteira | null;
    /**
     * `corrigir`: reescreve a anotação que já está no card, para quando foi
     * preenchida errado (#6290). Abre com o texto atual e não conta como
     * andamento novo.
     */
    modo?: 'registrar' | 'corrigir';
    /** Quem está logado — para avisar quando a correção é da anotação de outra pessoa. */
    usuarioId?: string;
    onClose: () => void;
    onConfirmar: (observacao: string) => Promise<void>;
}

/**
 * Registra o que foi feito numa demanda derivada (documento, negativação,
 * retorno).
 *
 * Não é um "concluir": estas filas se resolvem sozinhas quando o dado de origem
 * muda — a notificação sai de "aguardando assinatura" quando o Autentique
 * confirma, não quando alguém clica. O que falta registrar é o andamento, e é
 * isso que responde à pergunta que originou a esteira: "está sendo feito?".
 */
const AndamentoModal: React.FC<Props> = ({ demanda, modo = 'registrar', usuarioId, onClose, onConfirmar }) => {
    const [observacao, setObservacao] = useState('');
    const [salvando, setSalvando] = useState(false);
    const corrigir = modo === 'corrigir';

    useEffect(() => {
        setObservacao(corrigir ? demanda?.atribuicao?.observacao ?? '' : '');
    }, [demanda?.id, corrigir]);

    // Esc fecha o modal — sem isto a única saída é o X ou "Cancelar", e quem
    // está acostumado a teclado fica preso.
    useEffect(() => {
        if (!demanda) return;
        const aoTeclar = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', aoTeclar);
        return () => window.removeEventListener('keydown', aoTeclar);
    }, [demanda, onClose]);

    if (!demanda) return null;
    const meta = FILAS[demanda.fila];
    const anterior = demanda.atribuicao;
    const deOutraPessoa = corrigir && !!usuarioId && anterior?.ultima_acao_por !== usuarioId;
    const semMudanca = corrigir && observacao.trim() === (anterior?.observacao ?? '').trim();

    const confirmar = async () => {
        setSalvando(true);
        try { await onConfirmar(observacao); } finally { setSalvando(false); }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            onClick={onClose}
            role="presentation"
        >
            <div
                className="w-full max-w-lg bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden"
                onClick={e => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="andamento-modal-titulo"
            >
                <div className="flex items-start justify-between gap-4 p-5 border-b border-gray-100 dark:border-gray-700">
                    <div className="min-w-0">
                        <h2 id="andamento-modal-titulo" className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <NotebookPen className="w-4 h-4 text-primary" /> {corrigir ? 'Corrigir anotação' : 'Registrar andamento'}
                        </h2>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                            {meta.titulo} · {demanda.cliente_nome || 'Cliente sem nome'}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        aria-label="Fechar"
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 shrink-0"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="p-5 space-y-4">
                    {corrigir ? (
                        <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/40 rounded-lg p-3">
                            Anotação de <strong>{anterior?.ultima_acao_por_nome || 'alguém'}</strong>
                            {anterior?.ultima_acao_em ? <> em {formatDia(anterior.ultima_acao_em)}</> : null}.{' '}
                            {deOutraPessoa
                                ? 'Ao salvar, ela passa a aparecer com o seu nome e a data de hoje — o texto vai ser seu.'
                                : 'A correção troca o texto e mantém a data — não conta como andamento novo.'}
                        </div>
                    ) : (
                        <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/40 rounded-lg p-3">
                            {meta.instrucao}
                        </div>
                    )}

                    {!corrigir && anterior?.ultima_acao_em ? (
                        <div className="text-xs text-gray-600 dark:text-gray-300 border-l-2 border-primary/40 pl-3">
                            <strong>Último andamento</strong> — {anterior.ultima_acao_por_nome || 'alguém'} em{' '}
                            {formatDia(anterior.ultima_acao_em)}
                            {anterior.observacao ? <p className="mt-1 text-gray-500 dark:text-gray-400">{anterior.observacao}</p> : null}
                        </div>
                    ) : null}

                    <div>
                        <label className="block text-xs font-bold text-gray-600 dark:text-gray-300 mb-1.5">
                            {corrigir ? 'Texto da anotação' : 'O que foi feito?'}
                        </label>
                        <textarea
                            autoFocus
                            rows={4}
                            value={observacao}
                            onChange={e => setObservacao(e.target.value)}
                            placeholder="Ex.: cobrei a assinatura do procurador por WhatsApp, retorno prometido para sexta."
                            className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                        <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1.5">
                            {corrigir
                                ? 'Para tirar a anotação do card, feche aqui e use Excluir.'
                                : <>Isto não conclui a demanda — ela sai da fila sozinha quando o documento avançar.
                                    Serve para quem olhar depois saber que já está sendo cuidada.</>}
                        </p>
                    </div>
                </div>

                <div className="flex justify-end gap-2 p-5 border-t border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/30">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={confirmar}
                        disabled={salvando || !observacao.trim() || semMudanca}
                        className="px-4 py-2 text-sm font-bold bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                        {corrigir ? 'Salvar correção' : 'Registrar'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AndamentoModal;
